import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Form, useLocation, useNavigate } from "react-router-dom";
import { setToken } from "../../core/feature/token/tokenSlice";
import { setUser } from "../../core/feature/user/userSlice";
import { i18n } from "../../translations/i18n";
import { setLanguage } from "../../core/feature/language/languageSlice";
import { ChevronLeft } from "lucide-react";
import { credentialCheck } from "../../core/service/api";
import { setLoading } from "../../core/feature/config/configSlice";
import {
  getPerizinanType,
  postPerizinan,
} from "../../core/service/api_perizinan";
import { userInfo } from "os";

const PERIZINAN_ACTIVE_CALLER_ID_KEY = "perizinan_active_caller_id";
const PERIZINAN_ACTIVE_CALLER_KEY = "perizinan_active_caller";

const FormMobile = (props: any) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isToken, setIsToken] = useState<any>(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isAccOpen, setIsAccOpen] = useState(false);

  const user = useSelector((state: any) => state.user.user);
  const [types, setTypes] = useState<any[]>([]);

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");
  const allowedImageMimeTypes = ["image/png", "image/jpeg"];
  const allowedImageExtensions = [".png", ".jpg", ".jpeg"];

  const isAllowedImageFile = (selectedFile: File) => {
    const mimeType = selectedFile.type?.toLowerCase();
    if (mimeType && allowedImageMimeTypes.includes(mimeType)) {
      return true;
    }

    const fileName = selectedFile.name?.toLowerCase() || "";
    return allowedImageExtensions.some((ext) => fileName.endsWith(ext));
  };

  const toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  const [form, setForm] = useState({
    boardingLeaveTypeId: "",
    startDate: "",
    endDate: "",
    reason: "",
    teacherName: "",
    evidenceUrl: "",
  });

  const fetchType = async () => {
    try {
      const res = await getPerizinanType();
      const mapped = res.data.content
        .filter((item: any) => item.status === "ACTIVE")
        .map((item: any) => ({
          id: item.id,
          name: item.name,
        }));
      setTypes(mapped);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async () => {
    const storedCallerId = localStorage.getItem(PERIZINAN_ACTIVE_CALLER_ID_KEY);
    const storedCaller = localStorage.getItem(PERIZINAN_ACTIVE_CALLER_KEY);
    let parsedStoredCaller = null;

    try {
      parsedStoredCaller = storedCaller ? JSON.parse(storedCaller) : null;
    } catch (error) {
      localStorage.removeItem(PERIZINAN_ACTIVE_CALLER_KEY);
    }

    const caller = user?.accounts?.[0]?.callerIdentities?.[0];
    const resolvedCallerId =
      location.state?.activeCallerId ||
      storedCallerId ||
      parsedStoredCaller?.callerId ||
      caller?.callerId;
    const resolvedCallerName =
      location.state?.activeCallerName ||
      parsedStoredCaller?.name ||
      caller?.name;

    if (file && !isAllowedImageFile(file)) {
      setFileError("File harus berupa png, jpg, atau jpeg");
      return;
    }

    if (!resolvedCallerId) {
      toast.error("Data caller aktif tidak ditemukan");
      console.error("Active caller ID not found");
      return;
    }

    dispatch(setLoading(true));
    try {
      let base64 = "";
      if (file) {
        base64 = await toBase64(file);
      }
      const payload = {
        ...form,
        callerId: resolvedCallerId,
        callerName: resolvedCallerName,
        evidenceUrl: base64,
        requester: user.name,
      };

      await postPerizinan(payload);
      navigate("/perizinan");
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchType();
  }, []);

  return (
    <div className="h-screen flex flex-col px-4 py-4 bg-psp-primary font-montserrat">
      <div className="flex-none">
        <div className="flex d-flex w-full mb-4">
          <button>
            <ChevronLeft
              className="bg-white w-8 h-8 rounded-lg border p-1"
              onClick={() => navigate(`/perizinan`)}
            />
          </button>
          <div className="absolute left-1/2 -translate-x-1/2 font-bold text-md">
            Tambah Perizinan
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <section className="px-1">
          <div className="flex flex-col gap-1">
            <form className="">
              <div className="mb-4">
                <label className="block font-medium mb-2">Status</label>
                <select disabled className="w-full border-b bg-gray-200 p-2">
                  <option>Menunggu</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block font-medium mb-2">
                  Tipe Perizinan<span className="text-red">*</span>
                </label>
                <select
                  className="w-full border-b border-gray-400 bg-transparent p-2"
                  onChange={(e) =>
                    setForm({ ...form, boardingLeaveTypeId: e.target.value })
                  }
                >
                  <option value="">Pilih tipe perizinan</option>

                  {types.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block font-medium mb-2">
                  Tanggal Awal Izin<span className="text-red">*</span>
                </label>
                <input
                  type="date"
                  className="w-full border-b border-gray-400 bg-transparent p-2 focus:outline-none focus:black"
                  onChange={(e) =>
                    setForm({ ...form, startDate: e.target.value })
                  }
                />
              </div>
              <div className="mb-4">
                <label className="block font-medium mb-2">
                  Tanggal Akhir Izin<span className="text-red">*</span>
                </label>
                <input
                  type="date"
                  className="w-full border-b border-gray-400 bg-transparent p-2 focus:outline-none focus:black"
                  onChange={(e) =>
                    setForm({ ...form, endDate: e.target.value })
                  }
                />
              </div>
              <div className="mb-4">
                <label className="block font-medium mb-2">
                  Alasan<span className="text-red">*</span>
                </label>
                <textarea
                  className="w-full border-b border-gray-400 bg-transparent p-2 focus:outline-none focus:black"
                  placeholder="Input ..."
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                />
              </div>
              <div className="mb-4">
                <label className="block font-medium mb-2">
                  Guru Penanggung Jawab<span className="text-red">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border-b border-gray-400 bg-transparent p-2 focus:outline-none focus:black"
                  placeholder="Input ..."
                  onChange={(e) =>
                    setForm({ ...form, teacherName: e.target.value })
                  }
                />
              </div>
              <div className="mb-4">
                <label className="block font-medium mb-2">
                  Bukti Pendukung
                </label>
                <input
                  type="file"
                  id="name"
                  accept="image/*"
                  className="w-full border-b border-gray-400 bg-transparent p-2 focus:outline-none focus:black"
                  placeholder="Masukkan nama"
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0] || null;

                    if (!selectedFile) {
                      setFile(null);
                      setFileError("");
                      return;
                    }

                    if (!isAllowedImageFile(selectedFile)) {
                      setFile(null);
                      toast.error("File harus berupa png, jpg, atau jpeg");
                      e.target.value = "";
                      return;
                    }

                    setFile(selectedFile);
                    setFileError("");
                  }}
                />
                <p className="text-sm text-gray-500 m-0">
                  Format must be png, jpg, or jpeg
                </p>
                {fileError && (
                  <p className="text-sm text-red-500 m-0">{fileError}</p>
                )}
                <p className="text-sm text-red m-0">Max file 2 MB</p>
              </div>
            </form>
          </div>
        </section>
      </div>
      <div className="flex-none">
        <section className="mt-4 mb-1">
          <button
            className="w-full h-full py-2 bg-psp-btn-primary rounded-3xl text-white"
            onClick={() => setIsAccOpen(true)}
          >
            Ajukan Izin
          </button>
        </section>
      </div>
      <section>
        {isAccOpen && (
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity"></div>
        )}
        {isAccOpen && (
          <div className="fixed inset-0 z-50 flex items-end justify-center">
            <div className="absolute inset-0 bg-black/40"></div>
            <div className="relative w-full bg-white rounded-t-3xl py-4 px-2 animate-slideUp">
              <div className="flex items-center justify-between h-20 px-4">
                <div className="flex items-center">
                  <div className="flex flex-col leading-tight">
                    <strong>
                      <p className="text-lg">Konfirmasi</p>
                    </strong>
                    <p>Apakah anda yakin akan melakukan perizinan?</p>
                  </div>
                </div>
              </div>
              <div className="flex d-flex px-3 gap-3 mt-3 items-center justify-between">
                <button
                  className="bg-[#FF0000] rounded-full w-full h-full p-2 text-white text-sm"
                  onClick={() => {
                    setIsAccOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="bg-psp-btn-primary rounded-full w-full h-full p-2 text-white text-sm"
                  onClick={() => {
                    handleSubmit();
                    setIsAccOpen(false);
                  }}
                >
                  Simpan
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};
export default FormMobile;
